import { useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Lock,
  Pencil,
  Unlock,
  XCircle,
} from "lucide-react";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { FLASHPAY_ROLES } from "@/core/auth/roles";
import { RequireRole } from "@/core/auth/require-role";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCloseSavingsVault } from "@/features/savings/api/use-close-savings-vault";
import type { SavingsVaultDto } from "@/features/savings/api/dto";
import { VaultEarlyUnlockDialog } from "@/features/savings/components/vault-detail/vault-early-unlock-dialog";
import { VaultLockDialog } from "@/features/savings/components/vault-detail/vault-lock-dialog";
import { VaultMovementDialog } from "@/features/savings/components/vault-detail/vault-movement-dialog";
import { VaultRenameDialog } from "@/features/savings/components/vault-detail/vault-rename-dialog";

type OpenDialog = "deposit" | "withdraw" | "lock" | "rename" | "earlyUnlock";

/**
 * Barre d'actions du détail coffre : versement / retrait (ShareablePreview
 * avec section frais — 0 par défaut), blocage, renommage, clôture
 * (MutationConfirmPreview, sans frais) et déblocage anticipé en double
 * validation (réservé `flashpay-admin` / `flashpay-superadmin`).
 * Aucune action sur un coffre CLOSED.
 */
export function VaultActions({ vault }: { vault: SavingsVaultDto }) {
  const { t } = useTypedTranslation();
  const tSavings = (key: string) => t(key as TFunctionType);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const closeVault = useCloseSavingsVault(vault.id);

  const isOpen = vault.status === "OPEN";
  // Un coffre non vide ne peut pas être fermé (422 côté serveur) — le
  // bouton reste visible mais la règle est rappelée dans la preview.
  const hasBalance =
    (vault.balanceMinor ?? 0) !== 0 || (vault.pendingBalanceMinor ?? 0) !== 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("savings.vaults.actions.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          disabled={!isOpen}
          onClick={() => setDialog("deposit")}
        >
          <ArrowDownToLine className="mr-2 h-4 w-4" />
          {t("savings.vaults.actions.deposit")}
        </Button>
        <Button
          variant="secondary"
          disabled={!isOpen || vault.isLocked}
          onClick={() => setDialog("withdraw")}
        >
          <ArrowUpFromLine className="mr-2 h-4 w-4" />
          {t("savings.vaults.actions.withdraw")}
        </Button>
        <Button
          variant="secondary"
          disabled={!isOpen}
          onClick={() => setDialog("lock")}
        >
          <Lock className="mr-2 h-4 w-4" />
          {t("savings.vaults.actions.lock")}
        </Button>
        <Button
          variant="outline"
          disabled={!isOpen}
          onClick={() => setDialog("rename")}
        >
          <Pencil className="mr-2 h-4 w-4" />
          {t("savings.vaults.actions.rename")}
        </Button>
        <Button
          variant="destructive"
          disabled={!isOpen}
          onClick={() => setCloseOpen(true)}
        >
          <XCircle className="mr-2 h-4 w-4" />
          {t("savings.vaults.actions.close")}
        </Button>
        <RequireRole roles={[FLASHPAY_ROLES.ADMIN, FLASHPAY_ROLES.SUPERADMIN]}>
          <Button
            variant="outline"
            disabled={!isOpen || !vault.isLocked}
            onClick={() => setDialog("earlyUnlock")}
          >
            <Unlock className="mr-2 h-4 w-4" />
            {t("savings.vaults.actions.earlyUnlock")}
          </Button>
        </RequireRole>
      </CardContent>

      <VaultMovementDialog
        vault={vault}
        kind="deposit"
        open={dialog === "deposit"}
        onOpenChange={(open) => setDialog(open ? "deposit" : null)}
      />
      <VaultMovementDialog
        vault={vault}
        kind="withdraw"
        open={dialog === "withdraw"}
        onOpenChange={(open) => setDialog(open ? "withdraw" : null)}
      />
      <VaultLockDialog
        vault={vault}
        open={dialog === "lock"}
        onOpenChange={(open) => setDialog(open ? "lock" : null)}
      />
      <VaultRenameDialog
        vault={vault}
        open={dialog === "rename"}
        onOpenChange={(open) => setDialog(open ? "rename" : null)}
      />
      <VaultEarlyUnlockDialog
        vault={vault}
        open={dialog === "earlyUnlock"}
        onOpenChange={(open) => setDialog(open ? "earlyUnlock" : null)}
      />

      <MutationConfirmPreview
        open={closeOpen}
        onOpenChange={setCloseOpen}
        data={{
          vaultId: vault.id,
          vaultName: vault.name,
          currency: vault.currency,
          balanceMinor: vault.balanceMinor,
          pendingBalanceMinor: vault.pendingBalanceMinor,
        }}
        title={t("savings.vaults.close.title")}
        description={
          hasBalance
            ? t("savings.vaults.close.notEmptyDescription")
            : t("savings.vaults.close.description")
        }
        confirmLabel={t("savings.vaults.close.confirm")}
        t={tSavings}
        pending={closeVault.isPending}
        onConfirm={() =>
          closeVault.mutate(undefined, {
            onSuccess: () => {
              notifSilence(t("savings.vaults.feedback.closed"));
              setCloseOpen(false);
            },
            onError: (error) =>
              notifSilenceError(apiErrorHandler(error) || error.message),
          })
        }
      />
    </Card>
  );
}
